import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Trophy, ArrowDownLeft, ArrowUpRight, TrendingUp, TrendingDown, RefreshCw, Users, Percent, Coins, Clock, Layers, ChevronDown, CalendarDays, Eye } from 'lucide-react';
import { toast } from 'sonner';
import TicketDetailModal from './TicketDetailModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SPORT_LABELS = {
    americanfootball_nfl: 'NFL',
    americanfootball_ncaaf: 'NCAAF',
    basketball_nba: 'NBA',
    basketball_wnba: 'WNBA',
    basketball_ncaab: 'NCAAB',
    baseball_mlb: 'MLB',
    icehockey_nhl: 'NHL',
    boxing_boxing: 'Boxing',
    mma_mixed_martial_arts: 'MMA',
    soccer_epl: 'EPL',
    soccer_usa_mls: 'MLS',
};
const sportLabel = (k) => SPORT_LABELS[k] || (k ? k.split('_').slice(-1)[0].toUpperCase() : '—');

const statusPill = (s) => {
    const map = {
        won: 'bg-[#39FF14]/20 text-[#39FF14]',
        lost: 'bg-[#FF3B30]/20 text-[#FF3B30]',
        pending: 'bg-[#FFD700]/20 text-[#FFD700]',
        cashed_out: 'bg-[#00BFFF]/20 text-[#00BFFF]',
        void: 'bg-white/10 text-[#A0AAB2]',
    };
    return map[s] || 'bg-white/10 text-[#A0AAB2]';
};

const Tile = ({ label, period, testId }) => (
    <div className="rounded-lg border border-white/10 bg-[#0e0e0e] p-3" data-testid={testId}>
        <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold">{label}</p>
        <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <span className="text-[#8fbea4] flex items-center gap-1"><ArrowDownLeft className="w-3 h-3 text-[#39FF14]" />Handle</span>
            <span className="font-mono text-[#39FF14] text-right">{fmt(period.wagered)}</span>
            <span className="text-[#8fbea4] flex items-center gap-1"><ArrowUpRight className="w-3 h-3 text-[#FF3B30]" />Paid</span>
            <span className="font-mono text-[#FF3B30] text-right">{fmt(period.paid_out)}</span>
            <span className="text-[#8fbea4] flex items-center gap-1"><Coins className="w-3 h-3 text-[#FFD700]" />House net</span>
            <span className={`font-mono text-right font-black ${period.house_net >= 0 ? 'text-[#FFD700]' : 'text-[#FF3B30]'}`}>
                {period.house_net >= 0 ? '+' : ''}{fmt(period.house_net)}
            </span>
            <span className="text-[#8fbea4] flex items-center gap-1"><Percent className="w-3 h-3" />Hold</span>
            <span className="font-mono text-white text-right">{period.hold_pct === null || period.hold_pct === undefined ? '—' : `${period.hold_pct.toFixed(2)}%`}</span>
            <span className="text-[#8fbea4] flex items-center gap-1"><Layers className="w-3 h-3" />Bets (S/P)</span>
            <span className="font-mono text-white text-right">{period.count} <span className="text-[#666]">({period.singles}/{period.parlays})</span></span>
            <span className="text-[#8fbea4]">W / L</span>
            <span className="font-mono text-white text-right">{period.won}W / {period.lost}L</span>
            {period.cashed_out > 0 && (<>
                <span className="text-[#8fbea4]">Cashed out</span>
                <span className="font-mono text-[#00BFFF] text-right">{period.cashed_out}</span>
            </>)}
            <span className="text-[#8fbea4] flex items-center gap-1"><Users className="w-3 h-3" />Players</span>
            <span className="font-mono text-white text-right">{period.unique_players}</span>
            {period.pending > 0 && (<>
                <span className="text-[#8fbea4] flex items-center gap-1"><Clock className="w-3 h-3 text-[#FFD700]" />Open</span>
                <span className="font-mono text-[#FFD700] text-right">{period.pending} · {fmt(period.pending_liability)}</span>
            </>)}
        </div>
    </div>
);

/**
 * SportsbookReportCard — admin financial report for the sportsbook.
 * Reads GET /api/admin/reports/sportsbook (aggregated from db.sports_bets).
 * Shows today/week/month ins & outs, 7-day hold sparkline, per-sport
 * breakdown, recent bets, and top winners/losers (30d).
 */
export default function SportsbookReportCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [viewBet, setViewBet] = useState(null);
    const [viewLoading, setViewLoading] = useState(null);

    const openTicket = useCallback(async (betId) => {
        if (!betId) return;
        setViewLoading(betId);
        try {
            const { data } = await axios.get(`${API}/admin/sports/ticket/${betId}`, { withCredentials: true });
            setViewBet(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not load ticket');
        } finally { setViewLoading(null); }
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/reports/sportsbook`, { withCredentials: true });
            setData(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load Sportsbook report');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (!data) {
        return (
            <div className="card-dark p-5 text-[#A0AAB2] text-xs" data-testid="sb-report-loading">
                Loading Sportsbook report…
            </div>
        );
    }

    return (
        <div className="card-dark p-5 space-y-4" data-testid="sb-report-card">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-[#FFD700]" />
                    <h3 className="text-sm uppercase font-bold tracking-wide text-white">Sportsbook — Ins & Outs</h3>
                    <span className="text-[10px] text-[#A0AAB2]">handle · payouts · hold</span>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    className="text-xs text-[#A0AAB2] hover:text-white flex items-center gap-1"
                    data-testid="sb-report-refresh"
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Tile label="Today (CT)" period={data.today} testId="sb-report-today" />
                <Tile label="This week" period={data.week} testId="sb-report-week" />
                <Tile label="This month" period={data.month} testId="sb-report-month" />
            </div>

            <HoldSparkline data={data.daily_7d || []} />

            {/* Which tickets won each day (last 14 days) */}
            <DailyWinners days={data.daily_winners || []} onView={openTicket} viewLoading={viewLoading} />

            {/* Per-sport breakdown (this month) */}
            {(data.sports_breakdown || []).length > 0 && (
                <div>
                    <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold mb-2">By sport · this month</p>
                    <div className="rounded-lg border border-white/5 bg-[#0a0a0a] divide-y divide-white/5" data-testid="sb-report-sports">
                        {data.sports_breakdown.map((s) => (
                            <div key={s.label} className="flex items-center justify-between px-3 py-2 text-[11px]" data-testid={`sb-report-sport-${s.sport_key || 'parlay'}`}>
                                <span className="text-white font-bold w-20 shrink-0">{s.sport_key ? sportLabel(s.sport_key) : s.label}</span>
                                <span className="text-[#A0AAB2] font-mono flex-1 text-center">{s.count} bets · {fmt(s.wagered)} in</span>
                                <span className={`font-mono font-bold w-24 text-right ${s.house_net >= 0 ? 'text-[#FFD700]' : 'text-[#FF3B30]'}`}>
                                    {s.house_net >= 0 ? '+' : ''}{fmt(s.house_net)}
                                </span>
                                <span className="text-[#A0AAB2] font-mono w-14 text-right">{s.hold_pct === null ? '—' : `${s.hold_pct.toFixed(1)}%`}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Recent bets + top winners/losers */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div>
                    <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold mb-2">Recent bets</p>
                    <div className="rounded-lg border border-white/5 bg-[#0a0a0a] max-h-64 overflow-y-auto divide-y divide-white/5" data-testid="sb-report-recent">
                        {data.recent_bets.length === 0 ? (
                            <p className="text-[11px] text-[#A0AAB2] italic p-3">No bets placed yet.</p>
                        ) : data.recent_bets.map((r) => (
                            <button key={r.id} type="button" onClick={() => openTicket(r.id)}
                                className="w-full text-left flex items-center justify-between px-3 py-2 text-[11px] hover:bg-white/[0.04] transition-colors group" data-testid={`sb-report-row-${r.id}`}>
                                <div className="min-w-0 pr-2">
                                    <p className="text-white truncate flex items-center gap-1">
                                        {viewLoading === r.id
                                            ? <RefreshCw className="w-3 h-3 animate-spin text-[#FFD700] shrink-0" />
                                            : <Eye className="w-3 h-3 text-[#666] group-hover:text-[#FFD700] shrink-0" />}
                                        {r.name} <span className="text-[#666]">· {sportLabel(r.sport_key)}</span>
                                    </p>
                                    <p className="text-[#A0AAB2] truncate">{r.pick}{r.matchup ? ` · ${r.matchup}` : ''}</p>
                                    <p className="text-[#666]">
                                        {r.placed_at ? new Date(r.placed_at).toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) + ' CT' : ''}
                                    </p>
                                </div>
                                <div className="text-right shrink-0">
                                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${statusPill(r.status)}`}>{(r.status || '').toUpperCase()}</span>
                                    <p className="font-mono text-[#A0AAB2] mt-0.5">bet {fmt(r.amount)}</p>
                                    {r.net !== null && (
                                        <p className={`font-mono font-bold ${r.net > 0 ? 'text-[#39FF14]' : r.net < 0 ? 'text-[#FF3B30]' : 'text-[#FFD700]'}`}>
                                            {r.net >= 0 ? '+' : ''}{fmt(r.net)}
                                        </p>
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="space-y-3">
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold mb-2 flex items-center gap-1">
                            <TrendingUp className="w-3 h-3 text-[#39FF14]" />
                            Top winners (30d)
                        </p>
                        <div className="rounded-lg border border-white/5 bg-[#0a0a0a] divide-y divide-white/5" data-testid="sb-report-winners">
                            {data.top_winners_30d.length === 0 ? (
                                <p className="text-[11px] text-[#A0AAB2] italic p-3">No settled bets in last 30 days.</p>
                            ) : data.top_winners_30d.slice(0, 5).map((w) => (
                                <div key={w.user_id} className="flex items-center justify-between px-3 py-2 text-[11px]">
                                    <span className="text-white truncate">{w.name}</span>
                                    <span className="flex items-center gap-2">
                                        <span className="text-[#A0AAB2] font-mono">{w.bets}b</span>
                                        <span className={`font-mono font-bold ${w.net > 0 ? 'text-[#39FF14]' : 'text-[#A0AAB2]'}`}>{w.net >= 0 ? '+' : ''}{fmt(w.net)}</span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold mb-2 flex items-center gap-1">
                            <TrendingDown className="w-3 h-3 text-[#FF3B30]" />
                            Top losers (30d) — house wins from
                        </p>
                        <div className="rounded-lg border border-white/5 bg-[#0a0a0a] divide-y divide-white/5" data-testid="sb-report-losers">
                            {data.top_losers_30d.length === 0 ? (
                                <p className="text-[11px] text-[#A0AAB2] italic p-3">No settled bets in last 30 days.</p>
                            ) : data.top_losers_30d.slice(0, 5).map((w) => (
                                <div key={w.user_id} className="flex items-center justify-between px-3 py-2 text-[11px]">
                                    <span className="text-white truncate">{w.name}</span>
                                    <span className="flex items-center gap-2">
                                        <span className="text-[#A0AAB2] font-mono">{w.bets}b</span>
                                        <span className={`font-mono font-bold ${w.net < 0 ? 'text-[#FF3B30]' : 'text-[#A0AAB2]'}`}>{fmt(w.net)}</span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <TicketDetailModal bet={viewBet} onClose={() => setViewBet(null)} adminGrade onSettled={load} />
        </div>
    );
}

/**
 * DailyWinners — "which tickets won each day". One expandable row per CT day
 * (last 14 days) showing how many tickets won and total paid out; expand to see
 * every winning ticket (player, pick, stake → payout). Today is open by default.
 */
const DailyWinners = ({ days, onView, viewLoading }) => {
    const [openDay, setOpenDay] = useState(days[0]?.date || null);
    if (!days.length) {
        return (
            <div className="rounded-lg border border-white/5 bg-[#0a0a0a] p-4 text-center text-[11px] text-[#A0AAB2] italic" data-testid="sb-report-daily-winners-empty">
                No winning tickets in the last 14 days yet.
            </div>
        );
    }
    return (
        <div data-testid="sb-report-daily-winners">
            <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold mb-2 flex items-center gap-1">
                <CalendarDays className="w-3 h-3 text-[#39FF14]" />
                Winning tickets by day · last 14 days (CT)
            </p>
            <div className="rounded-lg border border-white/5 bg-[#0a0a0a] divide-y divide-white/5 overflow-hidden">
                {days.map((d) => {
                    const isOpen = openDay === d.date;
                    const label = new Date(d.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                    return (
                        <div key={d.date} data-testid={`sb-winners-day-${d.date}`}>
                            <button
                                type="button"
                                onClick={() => setOpenDay(isOpen ? null : d.date)}
                                className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-white/[0.04] transition-colors"
                                data-testid={`sb-winners-toggle-${d.date}`}
                            >
                                <span className="flex items-center gap-2 min-w-0">
                                    <ChevronDown className={`w-3.5 h-3.5 text-[#A0AAB2] shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                                    <span className="text-xs font-bold text-white">{label}</span>
                                    <span className="text-[10px] text-[#39FF14] bg-[#39FF14]/10 px-1.5 py-0.5 rounded-full font-bold">{d.count} won</span>
                                </span>
                                <span className="text-[11px] font-mono text-[#A0AAB2] shrink-0">
                                    paid <span className="text-[#FFD700] font-bold">{fmt(d.total_paid)}</span>
                                </span>
                            </button>
                            {isOpen && (
                                <div className="bg-black/30 divide-y divide-white/5" data-testid={`sb-winners-list-${d.date}`}>
                                    {d.tickets.map((t) => (
                                        <button key={t.id} type="button" onClick={() => onView && onView(t.id)}
                                            className="w-full text-left flex items-center justify-between px-3 py-2 pl-8 text-[11px] hover:bg-white/[0.04] transition-colors group" data-testid={`sb-winner-${t.id}`}>
                                            <div className="min-w-0 pr-2">
                                                <p className="text-white truncate flex items-center gap-1">
                                                    {viewLoading === t.id
                                                        ? <RefreshCw className="w-3 h-3 animate-spin text-[#FFD700] shrink-0" />
                                                        : <Eye className="w-3 h-3 text-[#666] group-hover:text-[#FFD700] shrink-0" />}
                                                    {t.name}
                                                    <span className="text-[#666]"> · {sportLabel(t.sport_key)}</span>
                                                    {t.status === 'cashed_out' && <span className="ml-1 text-[9px] text-[#00BFFF]">CASHED OUT</span>}
                                                </p>
                                                <p className="text-[#A0AAB2] truncate">{t.pick}{t.matchup ? ` · ${t.matchup}` : ''}</p>
                                            </div>
                                            <div className="text-right shrink-0 font-mono">
                                                <p className="text-[#A0AAB2]">{fmt(t.stake)} → <span className="text-[#39FF14] font-bold">{fmt(t.payout)}</span></p>
                                                <p className="text-[#39FF14]">+{fmt(t.profit)} profit</p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

/**
 * HoldSparkline — 7-day rolling line chart of daily hold %. Pure SVG.
 */
const HoldSparkline = ({ data }) => {
    const chart = useMemo(() => {
        const w = 600, h = 90, pad = 20;
        const withBets = data.filter((d) => d.count > 0);
        if (withBets.length === 0) return null;
        const numeric = data.map((d) => d.hold_pct).filter((v) => v !== null && v !== undefined);
        const maxAbs = Math.max(20, ...numeric.map((v) => Math.abs(v)));
        const yMin = -maxAbs, yMax = maxAbs;
        const stepX = (w - pad * 2) / Math.max(1, data.length - 1);
        const scaleX = (i) => pad + i * stepX;
        const scaleY = (v) => h - pad - ((v - yMin) / (yMax - yMin)) * (h - pad * 2);
        const segments = [];
        let seg = [];
        data.forEach((d, i) => {
            if (d.hold_pct === null || d.hold_pct === undefined) {
                if (seg.length) { segments.push(seg); seg = []; }
            } else {
                seg.push([scaleX(i), scaleY(d.hold_pct)]);
            }
        });
        if (seg.length) segments.push(seg);
        return { w, h, pad, segments, scaleX, scaleY, yMin, yMax };
    }, [data]);

    if (!chart) {
        return (
            <div className="rounded-lg border border-white/5 bg-[#0a0a0a] p-4 text-center text-[11px] text-[#A0AAB2] italic" data-testid="sb-report-sparkline-empty">
                No sports bets in the last 7 days — sparkline will populate as tickets are placed.
            </div>
        );
    }

    const { w, h, pad, segments, scaleX, scaleY, yMin, yMax } = chart;
    const zeroY = scaleY(0);

    return (
        <div className="rounded-lg border border-white/5 bg-[#0a0a0a] p-3 space-y-2" data-testid="sb-report-sparkline">
            <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold flex items-center gap-1">
                <Percent className="w-3 h-3" />
                Hold % · last 7 days
            </p>
            <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-24" preserveAspectRatio="none" role="img" aria-label="7-day sportsbook hold percentage">
                <line x1={pad} x2={w - pad} y1={zeroY} y2={zeroY} stroke="#FFD700" strokeDasharray="2 3" strokeWidth="1" opacity="0.35" />
                <text x={2} y={scaleY(yMax) + 4} fontSize="9" fill="#39FF14">{yMax.toFixed(0)}%</text>
                <text x={2} y={zeroY + 3} fontSize="9" fill="#FFD700">0</text>
                <text x={2} y={scaleY(yMin) + 4} fontSize="9" fill="#FF3B30">{yMin.toFixed(0)}%</text>
                {segments.map((seg, si) => (
                    <polyline
                        key={si}
                        fill="none"
                        stroke="#FFD700"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        points={seg.map(([x, y]) => `${x},${y}`).join(' ')}
                    />
                ))}
                {data.map((d, i) => {
                    if (d.hold_pct === null || d.hold_pct === undefined) return null;
                    const cx = scaleX(i);
                    const cy = scaleY(d.hold_pct);
                    return (
                        <g key={i}>
                            <circle cx={cx} cy={cy} r={3.5} fill={d.hold_pct < 0 ? '#FF3B30' : '#FFD700'} />
                            <title>{d.date} · {d.hold_pct.toFixed(2)}% hold · {d.count} bets · net ${d.house_net.toFixed(2)}</title>
                        </g>
                    );
                })}
            </svg>
            <div className="flex justify-between text-[9px] text-[#A0AAB2] font-mono px-4">
                {data.map((d, i) => (
                    <span key={i} title={`${d.date}: ${d.hold_pct === null ? 'no bets' : d.hold_pct.toFixed(2) + '% · ' + d.count + ' bets'}`}>
                        {new Date(d.date + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short' })}
                    </span>
                ))}
            </div>
        </div>
    );
};
